import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Link, useSearchParams } from "react-router";

import { type EncounterSpecialty, type GapDocState, getGapEncounters } from "../api/encounters";
import { ROUTES as AI_CODING_OUTPUTS_ROUTES } from "../ai-coding-outputs/navigation";
import { GapHint, STATE_DESCRIPTIONS } from "./gapInfo";
import { useGapBreakdown, useGapTrend } from "./useGapBreakdown";
import { toDateOnly } from "./useEncounterBreakdown";

function DrillCount({ count, onDrill }: { count: number; onDrill: () => void }) {
  if (count === 0) return <span>0</span>;
  return (
    <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={onDrill}>
      {count}
    </button>
  );
}

export function GapAnalysisSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const customers = searchParams.get("customers")?.split(",").filter(Boolean) ?? [];
  const from = searchParams.get("from") ?? undefined;
  const to = searchParams.get("to") ?? undefined;

  const dischargeDateFrom = toDateOnly(from);
  const dischargeDateTo = toDateOnly(to);

  const opts = { customerIds: customers.length ? customers : undefined, dateFrom: from, dateTo: to };
  const { data: gap } = useGapBreakdown(opts);
  const { data: trend } = useGapTrend(opts);

  const providerId = searchParams.get("provider_id");
  const specialty = searchParams.get("specialty");
  const docKey = searchParams.get("doc_key");
  const state = searchParams.get("state") as GapDocState | null;

  const drillTo = (drillProviderId: string, drillSpecialty: string, drillDocKey: string, drillState: GapDocState) => {
    const next = new URLSearchParams(searchParams);
    next.set("provider_id", drillProviderId);
    next.set("specialty", drillSpecialty);
    next.set("doc_key", drillDocKey);
    next.set("state", drillState);
    setSearchParams(next);
  };

  const { data: drill } = useQuery({
    queryKey: [
      "gap-encounters",
      customers.join(","),
      dischargeDateFrom,
      dischargeDateTo,
      providerId,
      specialty,
      docKey,
      state,
    ],
    enabled: Boolean(providerId && specialty && docKey && state),
    queryFn: () =>
      getGapEncounters({
        customerIds: customers.length ? customers : undefined,
        dischargeDateFrom,
        dischargeDateTo,
        providerId: providerId as string,
        specialty: specialty as EncounterSpecialty,
        docKey: docKey as string,
        state: state as GapDocState,
      }),
  });

  const drillEncounters = drill?.encounters ?? [];

  return (
    <div className="space-y-8 p-6">
      <h1 className="text-2xl font-semibold">Gap analysis</h1>

      {gap?.bySpecialty.map((branch) => {
        const points = trend?.byBranch.find(
          (b) => b.providerId === branch.providerId && b.specialty === branch.specialty,
        )?.points;
        return (
          <section key={`${branch.providerId}-${branch.specialty}`} className="space-y-2">
            <h2 className="text-lg font-medium">
              {branch.specialty} · {branch.providerId} · {branch.received} received
            </h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <GapHint description="The document (or 'one of' group) required to classify this specialty for this provider.">
                      Required doc
                    </GapHint>
                  </TableHead>
                  <TableHead>
                    <GapHint description="Where this required document comes from: the primary feed or the RPA scrape.">
                      Source
                    </GapHint>
                  </TableHead>
                  <TableHead>
                    <GapHint description={STATE_DESCRIPTIONS.present}>Present</GapHint>
                  </TableHead>
                  <TableHead>
                    <GapHint description={STATE_DESCRIPTIONS.missing_feed}>Missing (feed)</GapHint>
                  </TableHead>
                  <TableHead>
                    <GapHint description={STATE_DESCRIPTIONS.awaiting_scrape_not_triggerable}>
                      Awaiting (not triggerable)
                    </GapHint>
                  </TableHead>
                  <TableHead>
                    <GapHint description={STATE_DESCRIPTIONS.awaiting_scrape_triggerable}>
                      Awaiting (not returned)
                    </GapHint>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {branch.docs.map((d) => (
                  <TableRow key={d.key}>
                    <TableCell>
                      {d.displayKey}
                      {d.isOneOfGroup ? " (one of)" : ""}
                    </TableCell>
                    <TableCell>{d.source}</TableCell>
                    <TableCell>{d.counts.present}</TableCell>
                    <TableCell>
                      <DrillCount
                        count={d.counts.missing_feed}
                        onDrill={() => drillTo(branch.providerId, branch.specialty, d.key, "missing_feed")}
                      />
                    </TableCell>
                    <TableCell>
                      <DrillCount
                        count={d.counts.awaiting_scrape_not_triggerable}
                        onDrill={() =>
                          drillTo(branch.providerId, branch.specialty, d.key, "awaiting_scrape_not_triggerable")
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <DrillCount
                        count={d.counts.awaiting_scrape_triggerable}
                        onDrill={() =>
                          drillTo(branch.providerId, branch.specialty, d.key, "awaiting_scrape_triggerable")
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {points?.length ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={points}>
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="received" stroke="#64748b" />
                  <Line type="monotone" dataKey="classifiable" stroke="#10b981" />
                </LineChart>
              </ResponsiveContainer>
            ) : null}
          </section>
        );
      })}

      {drill ? (
        <section className="space-y-2">
          <h2 className="text-lg font-medium">
            {drillEncounters.length} encounters · {docKey} · {state}
          </h2>
          <ul className="space-y-1 text-sm">
            {drillEncounters.map((e) => (
              <li key={e.encounterId} className="flex flex-wrap items-center gap-x-4">
                <Link
                  className="text-primary underline-offset-2 hover:underline"
                  to={`${AI_CODING_OUTPUTS_ROUTES.unwrittenEncounterReview.route}/${e.encounterId}`}
                >
                  {e.encounterId}
                </Link>
                <span className="text-muted-foreground">MRN: {e.mrn ?? "—"}</span>
                <span className="text-muted-foreground">
                  Discharged: {e.dischargeDate ? dayjs(e.dischargeDate).format("YYYY-MM-DD") : "—"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
